export const uranusTree = {
  id: 'uranus',
  label: 'Uranus',
  type: 'Ice giant',
  summary: 'The sideways planet, tipped 98° and rolling through its orbit.',
  payload: [
    {
      type: 'description',
      text: "Uranus is the third-largest planet, and its atmosphere holds the coldest temperature measured on any planet. Its rotation axis is tipped almost perpendicular to its orbital plane, probably the result of a giant ancient collision. It rolls around the Sun on its side, meaning each pole experiences 42 years of continuous daylight followed by 42 years of night. Unlike Jupiter and Saturn, Uranus is an ice giant: beneath a hydrogen-helium atmosphere lies a mantle of water, methane, and ammonia under extreme pressure."
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '50,724 km (4 × Earth)' },
        { label: 'Mass',              value: '8.681 × 10²⁵ kg (14.5 × Earth)' },
        { label: 'Distance from Sun', value: '2,867 M km (19.2 AU)' },
        { label: 'Orbital period',    value: '84 Earth years' },
        { label: 'Day length',        value: '17h 14m (retrograde)' },
        { label: 'Axial tilt',        value: '97.77°, so it rolls on its side' },
        { label: 'Moons',             value: '29 known' },
        { label: 'Minimum temp',      value: '−224 °C (coldest planetary atmosphere)' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Size, density, gravity, magnetic field',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '25,559 km' },
            { label: 'Mean density',      value: '1.27 g/cm³' },
            { label: 'Surface gravity',   value: '9.01 m/s² (0.919 g, at 1-bar level)' },
            { label: 'Escape velocity',   value: '21.3 km/s' }
          ]
        },
        {
          type: 'stats',
          label: 'Magnetic',
          entries: [
            { label: 'Dipole tilt',    value: '59° from rotation axis' },
            { label: 'Offset',         value: 'Centred 1/3 radius off-axis (bizarre)' },
            { label: 'Source',         value: 'Ionic water-ammonia mantle, not iron core' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: "Uranus's magnetic field is tilted almost 60° from its rotation axis AND offset from the planet's centre. No one has a good explanation. Neptune has a similar quirk, suggesting it's a feature of ice giants."
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Hydrogen, helium, and enough methane to turn it blue',
      payload: [
        {
          type: 'description',
          text: "Uranus's atmosphere is ~83% hydrogen, ~15% helium, and ~2% methane by volume. The methane absorbs red light, which is why the planet looks pale cyan. Unlike Jupiter and Saturn, Uranus shows very few visible bands or storms. Voyager 2 famously called it 'featureless'. More recent imaging with adaptive optics has revealed some cloud structure, but it's subtle."
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Hydrogen (H₂)', percent: 83, color: '#6fb2d4' },
            { name: 'Helium (He)',   percent: 15, color: '#a9d8ec' },
            { name: 'Methane (CH₄)', percent: 2,  color: '#2a6ba8' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: "In 2006 Uranus got its first noticeable 'Great Dark Spot' seen by Hubble, a transient storm system about 1,700 km wide. Still nowhere near Jupiter-scale drama."
        }
      ]
    },
    {
      id: 'tilt',
      label: 'Why is it sideways?',
      summary: 'The 98° obliquity mystery',
      payload: [
        {
          type: 'description',
          text: "Uranus's rotation axis is tilted 97.77° from its orbital plane, so it effectively lies on its side. Every other planet spins more-or-less upright (Earth at 23.4°, Mars 25°, Jupiter 3°). The leading explanation: one or more giant impacts early in solar system history. A single glancing blow from an Earth-sized protoplanet could have done it. Alternative theories involve gravitational resonance with a since-lost moon."
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Each pole gets 42 years of continuous sunlight followed by 42 years of total darkness. Seasons on Uranus are unimaginable.'
        }
      ]
    },
    {
      id: 'rings',
      label: 'Rings',
      summary: '13 narrow, dark rings, very different from Saturn\'s',
      payload: [
        {
          type: 'description',
          text: "Uranus has 13 known rings: an inner set of nine narrow, dark rings and two fainter outer ones. The first nine were discovered in 1977 when Uranus occulted a star and the star's light dimmed before and after, which showed the rings. Unlike Saturn's bright icy rings, Uranus's inner rings are made of dark material, possibly radiation-processed organic compounds, and are shepherded by small moons."
        }
      ]
    },
    {
      id: 'moons',
      label: 'Major moons',
      summary: 'Five large moons, all named after Shakespeare and Pope characters',
      payload: [
        {
          type: 'list',
          entries: [
            { primary: 'Miranda',  secondary: '472 km: extreme, varied landscape, with fault canyons up to 12 times as deep as the Grand Canyon' },
            { primary: 'Ariel',    secondary: '1,158 km: youngest surface, signs of cryovolcanism' },
            { primary: 'Umbriel',  secondary: '1,169 km: darkest of the five' },
            { primary: 'Titania',  secondary: '1,578 km: largest, huge canyons' },
            { primary: 'Oberon',   secondary: '1,523 km: ancient, heavily cratered' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Every named Uranian moon is called after a Shakespeare character or a figure from Alexander Pope (the two most recent finds, S/2023 U1 and S/2025 U1, have no names yet). John Herschel started the tradition in 1852.'
        }
      ]
    },
    {
      id: 'exploration',
      label: 'Exploration',
      summary: 'Voyager 2, the only visitor',
      payload: [
        {
          type: 'description',
          text: "Only one spacecraft has ever visited Uranus: Voyager 2, in January 1986, during its grand tour of the outer planets. It came within 81,500 km of the cloud tops, returned photos of the moons, and discovered 10 new moons, the magnetic field, and two rings. Every image you've seen of Uranus's surface features came from Voyager 2 or later Earth-based telescopes with adaptive optics."
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'The Uranus Orbiter and Probe was named the top new flagship priority in the 2023–2032 Planetary Science Decadal Survey. If funded, it could launch by 2031 and arrive ~2045.'
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA Uranus fact sheet',   url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/uranusfact.html' },
            { label: 'Wikipedia: Uranus',         url: 'https://en.wikipedia.org/wiki/Uranus' },
            { label: 'Planetary Decadal Survey',  url: 'https://nap.nationalacademies.org/catalog/26522' },
            { label: 'NASA: Uranus facts',        url: 'https://science.nasa.gov/uranus/facts/' },
            { label: 'JPL: satellite discovery list', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
            { label: 'NASA: Miranda', url: 'https://science.nasa.gov/uranus/moons/miranda/' }
          ]
        }
      ]
    }
  ]
};
